import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, ArrowRight, Lock, TrendingUp } from 'lucide-react';

/**
 * Globally-rendered paywall dialog. Opened by `usePlanLimit` whenever the
 * backend returns a 409 with detail.code === 'limit_reached'.
 */
const UpgradeModal = ({ info, open, onClose }) => {
  const navigate = useNavigate();
  if (!info) return null;

  const next = info.next_plan_label || 'a higher plan';

  // Suggested upgrade target limit copy (rough — based on PLAN_LIMITS in backend)
  const NEXT_LIMITS = {
    starter: { users: 5, jobs: 25, candidates: 5000, clients: 50 },
    pro: { users: 20, jobs: 100, candidates: 50000, clients: 500 },
    enterprise: { users: 'unlimited', jobs: 'unlimited', candidates: 'unlimited', clients: 'unlimited' },
  };
  const nextLimit = NEXT_LIMITS[info.next_plan]?.[info.resource];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center mb-3">
            <Lock className="w-6 h-6 text-slate-900" />
          </div>
          <DialogTitle
            className="text-xl"
            data-testid="upgrade-modal-title"
          >
            You've hit your {info.plan_label} plan limit
          </DialogTitle>
          <DialogDescription className="text-sm">
            <span className="block mt-1">
              {info.message ||
                `You've used ${info.used} of ${info.limit} ${info.resource} on the ${info.plan_label} plan.`}
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* Visual: current vs next limit */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 my-2">
          <div className="grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">
                {info.plan_label}
              </div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {info.limit}
              </div>
              <div className="text-[10px] text-slate-500 capitalize">
                {info.resource}
              </div>
            </div>
            <div className="border-l border-slate-200 pl-3">
              <div className="text-[10px] uppercase tracking-wide text-teal-600 font-bold flex items-center justify-center gap-1">
                <TrendingUp className="w-3 h-3" /> {next}
              </div>
              <div className="text-2xl font-bold bg-gradient-to-r from-teal-500 to-emerald-600 bg-clip-text text-transparent mt-1">
                {nextLimit ?? '—'}
              </div>
              <div className="text-[10px] text-slate-500 capitalize">
                {info.resource}
              </div>
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-500 leading-relaxed">
          Unlock more {info.resource}, AI matching at scale, and the Recruiter Command Center for
          your whole team. Cancel anytime — your data exports cleanly.
        </p>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="upgrade-modal-later"
          >
            Maybe later
          </Button>
          <Button
            onClick={() => {
              onClose();
              navigate('/pricing');
            }}
            className="bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 hover:opacity-90 font-semibold"
            data-testid="upgrade-modal-cta"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            See upgrade options
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
